import { expressEventListener, expressQuerySelector, expressRemoveClass, expressAddClass } from "../../common/html";
import { createGrid, GridMode, IGrid, ISortValues } from "../technical/grid";
import { createPopup } from "../technical/popup-new";
import {
	deletePhotos,
	getGridBody,
	getGridItemAmount,
	getGridItems,
	movePhotos,
	renameAlbum,
} from "../../services/myphotos-service";
import { getCookie } from "../../utils/cookie";
import { showLoadingDialog } from "../popups/loading-dialog";
import { createUpload, FlowType } from "../technical/upload";
import { createPhotoDetailDialog, IPhotoDetailDialog } from "../popups/photo-detail-dialog";
import { createTextFieldEdit } from "../technical/text-field-edit";
import { createUploadDialog } from "../popups/upload-dialog";
import { createUploadIndicator } from "../technical/upload-indicator";
import { StoreType } from "../../services/storageService";
import { initDragAndDrop, IDragAndDropSettings } from "../technical/draganddrop";
import { getParameter } from "../../utils/querystring";
import { IPhoto, PhotoSourcesPopup } from "../organisms/photo-sources-popup";
import { createGeneralPageEventsAndComponents } from "../technical/page-general";
import { upload } from "../../services/upload-service";
import { removeGridItem, setupGridPageCacheHandlers, getPhotoGridEls } from "../../utils/grid-helper";
let grid: IGrid;

setupGridPageCacheHandlers(() => grid);

window.addEventListener("DOMContentLoaded", async () => {
	createGeneralPageEventsAndComponents();
	const gridEls = getPhotoGridEls();
	const MINIMUM_PHOTOS_FOR_ACTION = parseInt(gridEls.photoGridEl.dataset.minimumphotosforaction || "0", 10);
	const actionButton = expressQuerySelector<HTMLElement>(gridEls.photoGridEl, ".technical-action-button", false);
	const photoIndicatorEl = expressQuerySelector<HTMLElement>(gridEls.photoGridEl, ".technical-upload-indicator", true);

	const flowType = FlowType.MyAlbums;
	let photoDetail: IPhotoDetailDialog | null = null;

	const dragAndDropSettings = {
		dropElement: expressQuerySelector<HTMLElement>(document, "main", true),
		overlayElement: expressQuerySelector<HTMLElement>(document, ".technical-drag-and-drop-overlay", false),
		onDrop: function (files: FileList) {
			uploadFiles(files);
		},
	} as IDragAndDropSettings;

	const dragAndDrop = initDragAndDrop(dragAndDropSettings);

	const refreshActionButton = (forceDisable: boolean = false) => {
		const total = parseInt(gridEls.photoGridEl.dataset.total || "0", 10);
		if (!forceDisable && total >= MINIMUM_PHOTOS_FOR_ACTION) {
			expressRemoveClass(actionButton, "a-button--disabled");
		} else {
			expressAddClass(actionButton, "a-button--disabled");
		}
	};

	// On this page we force the sort values. They will always be the same & passed with data attributes from server side code
	const getSortValues = () => {
		const sortDirection = parseInt(gridEls.photoGridEl.dataset.forcesortdirection || "0", 10);
		const folderColumns = parseInt(gridEls.photoGridEl.dataset.forcefoldercolumns || "0", 10);
		return {
			sortDirection,
			folderColumns,
		} as ISortValues;
	};

	const fetchGridBody = (_: string, currentPage: number) => {
		const sortValues = getSortValues();
		return getGridBody(
			GridMode.Photo,
			StoreType.eXpress,
			currentPage,
			sortValues.sortDirection,
			sortValues.folderColumns,
			gridEls.folderId,
		);
	};

	const fetchItems = (currentPage: number, store: StoreType, pageToken: string, _: string) => {
		const sortValues = getSortValues();
		return getGridItems(
			GridMode.Photo,
			store,
			currentPage,
			pageToken,
			sortValues.sortDirection,
			sortValues.folderColumns,
			gridEls.folderId,
		);
	};

	const onDelete = async (
		itemIdsToDelete: string[],
		useDeselected: boolean,
		_nrOfSelectedItems: number,
		allSelected: boolean,
		refresh: () => Promise<void>,
		onDeleteAll: () => void,
	): Promise<void> => {
		const res = await createPopup({
			dialogId: "DeletePhotos",
			view: "GenericPopup",
			forceRefresh: true,
			data: {
				ExtraPopupCss: "p-default",
				TitleLabel: "Label.MyPages.MyAlbums.Dialog.DeletePhoto.Title",
				BodyLabel: "Label.MyPages.MyAlbums.Dialog.DeletePhoto.Info",
				OkButtonLabel: "Label.MyPhotos.Dialogs.DeletePhotos.OkButtonText",
				CancelButtonLabel: "Label.MyPhotos.Dialogs.DeletePhotos.CancelButtonText",
			},
		});
		if (!res.popup) return;
		const okButton = expressQuerySelector(res.popup, ".technical-general-popup-ok-button", true);
		const cancelButton = expressQuerySelector(res.popup, ".technical-general-popup-cancel-button", true);
		expressEventListener(okButton, "click", (_) => {
			showLoadingDialog(
				deletePhotos(itemIdsToDelete, gridEls.folderId, useDeselected).then((__1) => {
					if (allSelected && itemIdsToDelete.length == 0) {
						onDeleteAll();
						res.close();
						refreshActionButton();
						gridEls.pageTitleEl.scrollIntoView(true);
					} else {
						// Close the popup
						res.close();
						// Wait for the photo's to be deleted, then refresh
						setTimeout(async () => {
							await refresh();
							refreshActionButton();
						}, itemIdsToDelete.length * 10);
					}
				}),
				0,
				allSelected ? 1000 : itemIdsToDelete.length * 10,
			);
		});
		expressEventListener(cancelButton, "click", (__2) => res.close());
		await res.openAsync();
	};

	const onMove = async (selectedItemIds: string[], useDeselected: boolean) => {
		const dialogId = "MovePhotoToAlbum";
		return createPopup({
			dialogId,
			view: "MovePhotoToAlbum",
			forceRefresh: true,
			data: {
				accountId: getCookie("accid"),
				excludeFolder: gridEls.folderId,
			},
		}).then(async (res) => {
			if (!res.popup) return;
			const popup = res;
			const gridBodyEl = expressQuerySelector<HTMLElement>(res.popup, ".technical-grid-body", true);
			expressRemoveClass(gridBodyEl, "skeleton-loading");
			const closeAndRedirect = (redirectUri: string) => {
				popup.close();
				window.location.href = window.location.origin + redirectUri;
			};
			const onAlbumItemClick = (
				itemId: string,
				_item: HTMLElement,
				_sortValue: string,
				_refresh: () => Promise<void>,
			) => {
				movePhotos(false, useDeselected, selectedItemIds, gridEls.folderId, itemId).then((res) => {
					if (res && res.redirectUrl) closeAndRedirect(res.redirectUrl);
				});
			};

			const onNewAlbumItemClick = () => {
				movePhotos(true, useDeselected, selectedItemIds, gridEls.folderId).then((res) => {
					if (res && res.redirectUrl) closeAndRedirect(res.redirectUrl);
				});
			};

			const fetchAlbumGrid = (_: string, currentPage: number) => {
				const sortValues = getSortValues();
				return getGridBody(
					GridMode.Photo,
					StoreType.eXpress,
					currentPage,
					sortValues.sortDirection,
					sortValues.folderColumns,
					gridEls.folderId,
				);
			};
			const loadAlbums = (curPage: number, store: StoreType, pageToken: string) => {
				return getGridItems(GridMode.Album, store, curPage, pageToken, 0, 3, "", false, gridEls.folderId, false);
			};

			const gridEl = expressQuerySelector<HTMLElement>(res.popup, ".technical-grid", true);
			await createGrid(gridEl, gridEls.pageHeaderEl, {
				maxSelectableItems: 0,
				pageLogEl: null,
				orderPrints: null,
				onDelete: null,
				fetchGridBody: fetchAlbumGrid,
				onMove: null,
				onItemClick: onAlbumItemClick,
				onNewItemClick: onNewAlbumItemClick,
				fetchItems: loadAlbums,
			});

			await popup.openAsync();
		});
	};

	const onItemClick = async (itemId: string, _item: HTMLElement, _sortValue: string, refresh: () => Promise<void>) => {
		const onBeforeClose = () => {
			refresh();
		};
		if (photoDetail === null) {
			const sortValues = getSortValues();
			photoDetail = await showLoadingDialog(
				createPhotoDetailDialog(gridEls.folderId, sortValues.sortDirection, sortValues.folderColumns, "", {
					onBeforeClose: onBeforeClose,
					removeGridItem: removeGridItemHandler,
				}),
			);
		}
		photoDetail.open(itemId || "");
	};

	const uploadFiles = async (files: FileList) => {
		if (!files || files.length == 0) return;
		window.leavePageWatcher && window.leavePageWatcher.start();

		const uploadDialog = createUploadIndicator(files.length, photoIndicatorEl);
		dragAndDrop.disableDropEventOnIdentifiers(dragAndDropSettings.noDropElementIdentifiers);
		refreshActionButton(true);

		const onUploadFinished = async (
			_folderId: string,
			importedFileIds: string[] | null,
			_failedUploadAmount: number,
			failedFileNames: string[] | null,
		) => {
			window.leavePageWatcher && window.leavePageWatcher.cancel();

			await grid.refresh(true);

			refreshActionButton();

			if (photoDetail !== null) {
				photoDetail.dispose();
				photoDetail = null;
			}
			uploadDialog?.close();
		};

		const onFileUploaded = (amountUploadedFiles: number) => {
			uploadDialog && uploadDialog.updateProgress && uploadDialog.updateProgress(amountUploadedFiles);

			if (uploadDialog && uploadDialog.getRemaining && uploadDialog.getRemaining() > 10) grid.refresh(false);
		};

		const onBeforeUploadStart = () => {
			uploadDialog && uploadDialog.updateProgress && uploadDialog.updateProgress(0);
		};

		// add the uploadDialog and finish methods with settings to the upload component
		await createUpload(files, gridEls.folderId, "", getCookie("accid"), {
			onBeforeUploadStart,
			onUploadFinished,
			onFileUploaded,
			batchSize: gridEls.uploadBatchSize,
			flowType: flowType,
		});
	};

	const onAddPhotoClick = (refresh: () => Promise<void>) => {
		const onImportPhotosFinished = async (photos: IPhoto[]): Promise<void> => {
			window.leavePageWatcher && window.leavePageWatcher.cancel();
			if (!photos || photos.length === 0) {
				return;
			}
			await refresh();
			refreshActionButton();
		};
		const photoSources = new PhotoSourcesPopup(
			flowType,
			onImportPhotosFinished,
			1000,
			[],
			gridEls.photoGridEl.dataset.folderid,
			false,
			photoIndicatorEl,
			() => {
				grid.refresh(false);
			},
		);
		refreshActionButton(true);
		window.leavePageWatcher && window.leavePageWatcher.start();
		photoSources.openPhotoStoresPopup({
			onBeforeClose: () => {
				if (!photoSources.isUploadInProgress()) refreshActionButton();
			},
		});
	};

	const fetchItemAmount = () => {
		return getGridItemAmount(gridEls.folderId);
	};

	const itemsLoaded = () => {
		dragAndDrop.disableDragEventOnIdentifiers(dragAndDropSettings.noDragElementIdentifiers);
	};

	const removeGridItemHandler = async (itemId: string) => {
		removeGridItem(gridEls.photoGridEl, itemId);
	};

	if (gridEls.photoGridEl) {
		grid = (await createGrid(gridEls.photoGridEl, gridEls.pageHeaderEl, {
			pageLogEl: gridEls.pageLogEl,
			onDelete,
			fetchGridBody,
			onMove,
			onItemClick,
			onNewItemClick: onAddPhotoClick,
			fetchItems,
			fetchItemAmount,
			itemsLoaded,
		})) as IGrid;
		refreshActionButton();
	}

	if (gridEls.textFieldEditEl) {
		const onEditFinished = (text: string) => {
			return renameAlbum(gridEls.folderId, text);
		};
		createTextFieldEdit(
			gridEls.textFieldEditEl,
			{ onEditFinished },
			{ autoUpdateOnChange: false, updateOnEnter: true },
		);
	}

	grid?.updateGridScrollPosition(!!getParameter("currentpage"));
});
